import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';

import { useTranslation } from '@/utils/i18n';
import { useAuthStore } from '@/stores/AuthState';
import { updateFavoriteService } from '@/api/tracking';
import { fetchUserInfo } from '@/api/user';
import { toast } from '@/stores/toast';
import { ButtonSystem } from '@/components/Buttons';
import ServiceLogo from '@/components/ServiceLogo';
import { STREAMING_SERVICES, normalizeServiceId } from '@/constants/streamingServices';
import { cn } from '@/lib/utils';

/**
 * "Ton service de streaming" — compact selectable list of the 5
 * services; the favorite is ringed + checked. Ported from the legacy
 * `StreamingServicesPrompt`. Selecting one persists it and refreshes the user.
 */
export default function StreamingServiceSelector({ className }: { className?: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const favorite = useAuthStore((s) => s.userInfo.favorite_streaming_service);
  const setUserInfo = useAuthStore((s) => s.setUserInfo);
  const favoriteNorm = normalizeServiceId(favorite);

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      await updateFavoriteService(id);
      return fetchUserInfo();
    },
    onSuccess: (user) => {
      void setUserInfo(user as never);
      toast.success(t('toast.favUpdated'));
      // Reload the home feed so the latest recommendation shows.
      void queryClient.invalidateQueries({ queryKey: ['discover-track'] });
    },
    onError: () => toast.error(t('toast.favError')),
  });

  return (
    <div className={className} data-testid="streaming-service-selector">
      <div className="tw:mb-2 tw:flex tw:flex-col tw:items-center tw:gap-0.5 tw:text-center">
        <span className="tw:text-[24px] tw:leading-none" aria-hidden="true">
          🔗
        </span>
        <h2 className="tw:heading-3 tw:text-white">{t('recommendation.streaming.connectTitle')}</h2>
        <p className="tw:caption tw:text-grey-2">
          {t('recommendation.streaming.connectDescription')}
        </p>
      </div>

      <div className="tw:flex tw:flex-col tw:gap-2">
        {STREAMING_SERVICES.map((svc) => {
          const isFavorite = favoriteNorm === svc.id;
          return (
            <div
              key={svc.id}
              className={cn(
                'tw:relative tw:rounded-2xl',
                isFavorite && 'tw:ring-2 tw:ring-green-primary',
              )}
            >
              <ButtonSystem
                variant="secondary"
                size="s"
                label={svc.name}
                leftIcon={<ServiceLogo service={svc.id} size={20} />}
                onClick={() => mutation.mutate(svc.id)}
                loading={mutation.isPending && mutation.variables === svc.id}
                disabled={mutation.isPending}
                data-testid={`btn-service-${svc.id}`}
              />
              {isFavorite && (
                <span className="tw:absolute tw:right-4 tw:top-1/2 tw:flex tw:size-5 tw:-translate-y-1/2 tw:items-center tw:justify-center tw:rounded-full tw:bg-green-primary tw:text-dark-primary">
                  <Check size={13} strokeWidth={3} />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
